/**
 * Shared styled components for flight-data tables (Flights, Nexus, …).
 * Lifted verbatim from the original copies on each page so both surfaces
 * stay visually identical. New consumers should pull from here rather
 * than redefining these shells.
 */

import { styled } from "styled-components";

/* ── Filter card ──────────────────────────────────────────────────── */

export const FilterCard = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.colors.paper};
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  padding: 20px;
`;

export const FilterForm = styled.form`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  color: ${(props) => props.theme.colors.grey};
  margin-bottom: 2px;
`;

export const FilterInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  border-radius: 5px;
  font-size: 14px;
  background-color: ${(props) => props.theme.colors.paper};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.blue};
  }
`;

export const FilterSelect = styled.select`
  padding: 8px 10px;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  border-radius: 5px;
  font-size: 14px;
  background-color: ${(props) => props.theme.colors.paper};
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.blue};
  }
`;

export const FilterCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

export const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding-bottom: 4px;
`;

export const AutoRefreshGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding-bottom: 4px;
  margin-left: auto;

  @media (max-width: 600px) {
    margin-left: 0;
  }
`;

export const SearchButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 5px;
  background-color: ${(props) => props.theme.colors.blue};
  color: ${(props) => props.theme.colors.white};
  font-size: 14px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.blueHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ConcourseChipRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => props.theme.colors.lightGrey};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ConcourseChip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s,
    border-color 0.15s;
  border: 1px solid
    ${(props) =>
      props.$active ? props.theme.colors.blue : props.theme.colors.lightGrey};
  background-color: ${(props) =>
    props.$active ? props.theme.colors.blue : props.theme.colors.paper};
  color: ${(props) =>
    props.$active ? props.theme.colors.white : props.theme.colors.grey};

  &:hover {
    border-color: ${(props) => props.theme.colors.blue};
    color: ${(props) =>
      props.$active ? props.theme.colors.white : props.theme.colors.blue};
  }
`;

/* ── Status banners ───────────────────────────────────────────────── */

export const ErrorBanner = styled.div`
  width: 100%;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.colors.danger};
  background-color: #fef2f2;
  color: ${(props) => props.theme.colors.danger};
  font-size: 14px;
`;

export const LoadingText = styled.p`
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: ${(props) => props.theme.colors.grey};
  font-size: 16px;
`;

export const EmptyMessage = styled.p`
  padding: 20px;
  color: ${(props) => props.theme.colors.grey};
  font-size: 14px;
`;

/* ── Table chrome ─────────────────────────────────────────────────── */

export const Table = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.paper};
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
`;

export const TableTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  padding: 20px;
`;

export const CountBadge = styled.span`
  font-size: 13px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  border-radius: 12px;
  padding: 2px 10px;
`;

export const TableScrollWrapper = styled.div`
  overflow-x: auto;
`;

export const TableBody = styled.div<{ $minWidth?: number }>`
  padding: 0 12px;
  min-width: ${(props) => props.$minWidth ?? 700}px;

  & > div:last-child {
    border-bottom: none;
  }
`;

export const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 13px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  padding: 10px 6px;
  width: 100%;
  color: ${(props) => props.theme.colors.grey};
`;

export const SortableHeaderCell = styled.div<{ $sortable: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  text-align: center;
  cursor: ${(props) => (props.$sortable ? "pointer" : "default")};
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: ${(props) =>
      props.$sortable ? props.theme.colors.black : "inherit"};
  }
`;

export const FlightTableRow = styled.div<{
  $isPast: boolean;
  $clickable?: boolean;
}>`
  padding: 10px 6px;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
  border-bottom: 1px solid ${(props) => props.theme.colors.lightGrey};
  width: 100%;
  opacity: ${(props) => (props.$isPast ? 0.5 : 1)};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};

  &:hover {
    background-color: ${(props) =>
      props.$clickable ? props.theme.colors.background : "transparent"};
  }
`;

export const FlightCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  text-align: center;
`;

/* ── Inline content primitives ────────────────────────────────────── */

export const AirlineCode = styled.span`
  color: ${(props) => props.theme.colors.blue};
  font-weight: ${(props) => props.theme.fontWeight.semibold};
`;

export const MonoText = styled.span`
  font-family: monospace;
  font-size: 13px;
`;

/**
 * Recent-change indicators shared by Flights and Nexus.
 *
 * - "time"  highlights values that updated within the freshness window
 *           (mission_time on Flights, sked/est/actual on Nexus).
 * - "gate"  highlights gate or pier changes within the same window.
 * - "none"  is the no-highlight default.
 *
 * Both pages drive these from a `field_updated_at` map (see Missions /
 * Nexus stores), so the rendering and palette are identical.
 */
export type HighlightKind = "gate" | "time" | "none";

export const HIGHLIGHT_COLORS: Record<HighlightKind, string> = {
  gate: "rgba(206, 38, 38, 0.15)",
  time: "rgba(9, 168, 63, 0.15)",
  none: "transparent",
};

export const DOT_COLORS: Record<Exclude<HighlightKind, "none">, string> = {
  gate: "#ce2626",
  time: "#09a83f",
};

export const HighlightCell = styled(FlightCell)<{ $highlight: HighlightKind }>`
  background-color: ${(props) => HIGHLIGHT_COLORS[props.$highlight]};
  border-radius: 4px;
  padding: 2px 4px;
`;

export const ChangeDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  flex-shrink: 0;
`;

/**
 * Relative-time label ("in 12m", "1h 5m ago"). Dimmed when the
 * referenced moment is already in the past, matching the row dimming
 * convention used on Flights and Nexus.
 */
export const RelativeText = styled.span<{ $isPast: boolean }>`
  font-size: 13px;
  white-space: nowrap;
  color: ${(props) =>
    props.$isPast ? props.theme.colors.grey : props.theme.colors.black};
`;

/**
 * Status badge for the live-feed time type:
 *   A = Actual  (green)
 *   E = Estimated (blue)
 *   S = Scheduled / other (grey)
 *
 * Used by both the Flights page (live feed `time_type`) and the Nexus
 * page (derived from which of actual_out/est_out/sked_out is populated).
 */
export const TimeTypeBadge = styled.span<{ $type: string }>`
  font-size: 10px;
  font-weight: ${(props) => props.theme.fontWeight.medium};
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  background-color: ${(props) => {
    switch (props.$type) {
      case "A":
        return "rgba(9, 168, 63, 0.12)";
      case "E":
        return "rgba(3, 148, 232, 0.12)";
      default:
        return "rgba(0, 0, 0, 0.06)";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "A":
        return props.theme.colors.green;
      case "E":
        return props.theme.colors.blue;
      default:
        return props.theme.colors.grey;
    }
  }};
`;
